import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../../components/Modal";
import { ModelCxContext } from "../../context/modelCx.context";
import { CONVEX_CONFIGURED, resolveBackendMode } from "../../utility/backendConfig";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
} from "../../utility/keys";
import { refs } from "../../convex/apiRefs";
import { convexClient } from "../../convex/convexClient";
import { convexReadEventLog } from "../../convex/convexRead";
import { buildEventLogByDocumentIdRequestBody } from "../../utility/buildEventLogByDocumentIdRequestBody";
import { fetchAllLambdaChunks } from "../../utility/fetchAllLambdaChunks";
import { parseEventLogResponse } from "../../utility/parseEventLogResponse";
import { buildResolveInvoiceRequestBody } from "../../utility/ticketMaster/buildResolveInvoiceRequestBody";
import { buildResolveInvoiceFromEventLogRequestBody } from "../../utility/ticketMaster/buildResolveInvoiceFromEventLogRequestBody";
import { pickRecoveredJobDoc, formatRecoveredDate } from "../../utility/ticketMaster/pickRecoveredJobDoc";
import { buildConclusionContext } from "../../utility/ticketMaster/buildConclusionContext";
import { getInvoiceHref } from "../../utility/fileMakerInvoiceHref";
import JobHistorySection from "../../components/JobHistorySection";
import TicketMasterTimeline from "./TicketMasterTimeline";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — the 🎫 Help-Desk ticket research tool.
//
// Type an invoice → a CORRELATED timeline: one shared, scrolling grid whose
// rows are moments and whose columns are the three lanes (EventLog · FileMaker
// Schedule [DATA] · FileMaker TurboQ). A Scheduler edit, the TurboQ message it
// pushed to FileMaker, and the FileMaker schedule row it produced line up in the
// same row. Ascend/descend + a correlation window are user-controlled.
//
// READ-ONLY, PRODUCTION DATA (like Compass + Time Machine — the audit
// warehouses were never replicated to UAT, so there is no env toggle). Lane ①
// reads from whichever backend the signed-in user is on (convexOnly → Convex,
// else Mongo). The FileMaker columns are FileMaker data. Gated by the
// "ticketMaster" screenAccessList token.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const PROD_DB = "Warehouses";
const OBJECTID_HEX_RE = /^[0-9a-fA-F]{24}$/;

async function postQuery(body) {
  const response = await fetch(MONGODB_AWSGW_QUERIES, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": MONGODB_AWSGW_APIKEY,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Ticket Master query HTTP ${response.status}`);
  return response.json();
}

function Fact({ label, value, href }) {
  const display = value == null || value === "" ? "—" : String(value);
  return (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-wide opacity-55">{label}</span>
      {href && display !== "—" ? (
        <a className="font-mono text-xs text-skin-link underline hover:opacity-75"
          href={href} target="_blank" rel="noreferrer" title="Open this invoice in FileMaker">
          {display}
        </a>
      ) : (
        <span className="font-mono text-xs">{display}</span>
      )}
    </span>
  );
}

function TicketMasterModal({ setOpenTicketMasterModal }) {
  const close = () => setOpenTicketMasterModal(false);

  const { tsUserDocument } = useContext(ModelCxContext) || {};
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";
  const useConvexEvents = isConvexOnly && CONVEX_CONFIGURED;

  const [invoice, setInvoice] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [order, setOrder] = useState("asc");
  const [schedulerOnly, setSchedulerOnly] = useState(true);
  const [conclusion, setConclusion] = useState(null); // { ok, conclusion?, error? }
  const [concluding, setConcluding] = useState(false);
  const [copied, setCopied] = useState(false); // false | true | "error"
  const [conclusionExpanded, setConclusionExpanded] = useState(false); // #292 item 4
  const [showHistory, setShowHistory] = useState(false); // #292 item 5 — on-demand TM history

  const onLookup = () => {
    const v = invoice.trim();
    if (v) {
      setSubmitted(v);
      setConclusion(null);
      setCopied(false);
      setShowHistory(false);
    }
  };

  // Copy the conclusion (with an invoice header so it's self-describing when
  // pasted into a ticket) and flash a "copied" notice.
  const onCopyConclusion = async () => {
    if (!conclusion?.ok) return;
    const text = `Ticket Master conclusion — invoice ${submitted}\n\n${conclusion.conclusion}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied("error");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // Step 1 — resolve invoice → ActiveQueue doc (PROD warehouse, Lambda).
  //
  // #417 — two-step. ActiveQueue is a working queue, not an archive: a finished
  // job gets purged from it, and since every lane below is gated on this doc's
  // ObjectId, the tool used to render completely empty for exactly the tickets
  // help desk opens it to research. When the queue lookup comes up empty we
  // recover the job from EventLog, whose `documentKey` preserves the
  // invoice→documentId link and whose last stored image is the full job doc.
  const { data: resolved, isFetching: resolving, error: resolveError } = useQuery({
    queryKey: ["ticketMasterResolve", submitted],
    enabled: Boolean(submitted),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () => {
      const body = buildResolveInvoiceRequestBody({ invoice: submitted, database: PROD_DB, dataSource: MONGODB_DATASOURCE_TS });
      const json = await postQuery(body);
      const docs = json?.message?.result?.result ?? [];
      const doc = docs.find((d) => String(d.invoiceNo) === String(submitted)) || docs[0] || null;
      if (doc) return { doc, recoveredFrom: null };

      const fallbackBody = buildResolveInvoiceFromEventLogRequestBody({ invoice: submitted, database: PROD_DB });
      const fallbackJson = await postQuery(fallbackBody);
      const recovered = pickRecoveredJobDoc(fallbackJson?.message?.result);
      if (!recovered) return { doc: null, recoveredFrom: null };
      return { doc: recovered.doc, recoveredFrom: recovered.recoveredFrom };
    },
  });

  const doc = resolved?.doc || null;
  // Non-null only when the job was rebuilt from history — drives the notice
  // above the facts row so a stale snapshot is never read as live state.
  const recoveredFrom = resolved?.recoveredFrom || null;
  const documentId = typeof doc?._id === "string" ? doc._id : null;
  const validDocId = documentId != null && OBJECTID_HEX_RE.test(documentId);
  const kellerId = typeof doc?._kf_KellerID === "string" ? doc._kf_KellerID : "";
  const companyCode = doc?.companyCode ? String(doc.companyCode) : "";
  // #291 — extra transaction lookup keys: scripts key the job by different
  // primary keys, so lane ③ searches by ALL of them. projectNo = lowes_projectNo
  // (the WO#); activeQueueId = the FileMaker ActiveQueue UUID (Mongo doc `id`).
  const projectNo = doc?.lowes_projectNo != null ? String(doc.lowes_projectNo) : "";
  const activeQueueId = typeof doc?.id === "string" ? doc.id : "";

  // Lane ① — EventLog (source per backend).
  const { data: eventData, isFetching: fetchingEvents } = useQuery({
    queryKey: ["ticketMasterEventLog", documentId, useConvexEvents ? "cx" : "lambda"],
    enabled: Boolean(submitted) && validDocId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () => {
      if (useConvexEvents) {
        const raw = await convexReadEventLog(refs.getEventLogByDocumentId, { targetCollection: "ActiveQueue", documentId }, "getEventLogByDocumentId");
        return parseEventLogResponse(raw, "getEventLogByDocumentId");
      }
      const body = buildEventLogByDocumentIdRequestBody({ targetCollection: "ActiveQueue", documentId, database: PROD_DB });
      // #163 — follow the Lambda chunk cursor so a long EventLog is never
      // truncated by the 6 MB API Gateway cap.
      const raw = await fetchAllLambdaChunks(
        (cursor) => postQuery(cursor ? { ...body, cursor } : body),
        ["events"]
      );
      return parseEventLogResponse(raw, "getEventLogByDocumentId");
    },
  });

  // Lanes ②③ — FileMaker (Convex read path), separate queries → own spinners.
  const fmEnabled = Boolean(submitted) && validDocId && CONVEX_CONFIGURED;
  const callFm = (lane) =>
    convexClient.action(refs.ticketMasterReadFileMakerLanes, { invoice: submitted, kellerId, projectNo, activeQueueId, companyCode, lane });

  const { data: scheduleData, isFetching: fetchingSchedule, error: scheduleErr } = useQuery({
    queryKey: ["ticketMasterFm", "schedule", documentId],
    enabled: fmEnabled, retry: false, refetchOnWindowFocus: false, staleTime: 60_000,
    queryFn: () => callFm("schedule"),
  });
  const { data: turboData, isFetching: fetchingTurbo, error: turboErr } = useQuery({
    queryKey: ["ticketMasterFm", "turboq", documentId],
    enabled: fmEnabled, retry: false, refetchOnWindowFocus: false, staleTime: 60_000,
    queryFn: () => callFm("turboq"),
  });

  // Normalize each lane into rows + an error string.
  const eventRows = eventData?.ok ? eventData.events : [];
  const eventError = eventData && eventData.ok === false ? `EventLog error: ${eventData.reason || "unknown"}` : null;

  const scheduleRows = scheduleData?.schedule?.rows || [];
  const scheduleError = scheduleErr
    ? String(scheduleErr.message || scheduleErr)
    : scheduleData?.ok === false ? scheduleData.error : scheduleData?.schedule?.error || null;

  const turboRows = turboData?.turboq?.rows || [];
  const turboError = turboErr
    ? String(turboErr.message || turboErr)
    : turboData?.ok === false ? turboData.error : turboData?.turboq?.error || null;

  const loading = useMemo(
    () => ({
      eventLog: Boolean(submitted) && (resolving || (validDocId && fetchingEvents)),
      schedule: Boolean(submitted) && (resolving || (fmEnabled && fetchingSchedule)),
      turboq: Boolean(submitted) && (resolving || (fmEnabled && fetchingTurbo)),
    }),
    [submitted, resolving, validDocId, fetchingEvents, fmEnabled, fetchingSchedule, fetchingTurbo]
  );

  const notFound = Boolean(submitted) && !resolving && !resolveError && !doc;

  // 🤖 Claude conclusion — serialize the assembled timeline and ask Convex.
  const onConclude = async () => {
    if (!submitted || !validDocId) return;
    setConcluding(true);
    setConclusion(null);
    setCopied(false);
    try {
      const context = buildConclusionContext({
        doc, eventLog: eventRows, schedule: scheduleRows, turboq: turboRows, schedulerOnly,
      });
      const res = await convexClient.action(refs.ticketMasterConclude, { context, invoice: submitted });
      setConclusion(res);
    } catch (e) {
      setConclusion({ ok: false, error: String(e.message || e) });
    } finally {
      setConcluding(false);
    }
  };

  return (
    <Modal onClose={close} disableCloseOnClickOutSide>
      <Modal.Header>
        <span className="flex items-center gap-2">
          <span aria-hidden="true">🎫</span>
          <span className="font-bold">Ticket Master</span>
          <span className="text-xs text-skin-link">Help Desk ticket research — read-only · PROD</span>
        </span>
      </Modal.Header>

      <Modal.Content>
        {/* User-resizable window (drag the bottom-right grip). Content never
            resizes the window — it scrolls inside the timeline body. */}
        <div
          className="flex flex-col gap-2 min-h-0"
          style={{
            width: "90vw", height: "84vh",
            minWidth: 520, minHeight: 360, maxWidth: "98vw", maxHeight: "92vh",
            resize: "both", overflow: "hidden",
          }}
        >
          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap text-xs shrink-0">
            <label className="flex items-center gap-2">
              <span className="text-skin-link">Invoice #</span>
              <input
                value={invoice}
                onChange={(e) => setInvoice(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onLookup()}
                placeholder="e.g. 2542494"
                className="text-black rounded-sm px-2 py-1 w-40 border-2 border-dark-grey"
              />
            </label>
            <button type="button" onClick={onLookup} disabled={!invoice.trim()}
              className="px-3 py-1 border-2 border-skin-link bg-skin-base rounded-sm font-medium shadow-xs hover:bg-skin-base-hover disabled:opacity-40">
              Look up
            </button>

            <span className="w-px h-5 bg-skin-base" />
            <button type="button" onClick={() => setOrder((o) => (o === "asc" ? "desc" : "asc"))}
              className="px-2 py-1 border-2 border-skin-base rounded-sm hover:bg-skin-base-hover">
              {order === "asc" ? "⬆ Oldest first" : "⬇ Newest first"}
            </button>
            <button type="button" onClick={() => setSchedulerOnly((s) => !s)}
              title="Transactions column: show only the UpdateInvoiceScheduling script, or every script that touched this invoice"
              className="px-2 py-1 border-2 border-skin-base rounded-sm hover:bg-skin-base-hover">
              {schedulerOnly ? "Transactions: Scheduling only" : "Transactions: All scripts"}
            </button>

            {resolving ? <span className="opacity-60">Resolving…</span> : null}
            {resolveError ? <span className="italic" style={{ color: "#ff6b6b" }}>Resolve failed: {String(resolveError.message || resolveError)}</span> : null}
            {notFound ? <span className="italic opacity-70">No document found for invoice {submitted}.</span> : null}
          </div>

          {/* #417 — the job has left the active queue; everything below was
              rebuilt from its last recorded state, so say so. */}
          {doc && recoveredFrom ? (
            <div
              data-testid="tm-recovered-notice"
              className="border rounded-sm px-3 py-1.5 text-xs shrink-0"
              style={{ borderColor: "#d99a2b", color: "#e0a838" }}
            >
              <span aria-hidden="true">⚠ </span>
              This job is no longer in the active queue. The details below are from its last
              recorded state{formatRecoveredDate(recoveredFrom.timestamp)
                ? ` on ${formatRecoveredDate(recoveredFrom.timestamp)}`
                : ""}
              .
            </div>
          ) : null}

          {/* Current-state header */}
          {doc ? (
            <div className="border border-skin-base rounded-sm px-3 py-1.5 flex flex-wrap gap-x-5 gap-y-1.5 bg-skin-base shrink-0">
              <Fact label="Invoice" value={doc.invoiceNo} href={doc.invoiceNo ? getInvoiceHref(doc.invoiceNo) : undefined} />
              <Fact label="Customer" value={`${doc.customer_firstName ?? ""} ${doc.customer_lastName ?? ""}`.trim()} />
              <Fact label="Company" value={doc.companyCode} />
              <Fact label="Location" value={doc.location} />
              <Fact label="Store" value={doc.lowes_storeNo ?? doc.storeNo} />
              <Fact label="Status" value={doc.statusCode} />
              <Fact label="Installer" value={Array.isArray(doc.installer) ? doc.installer.join(", ") : doc.installer} />
              <Fact label="Keller ID" value={doc._kf_KellerID} />
            </div>
          ) : null}

          {/* 🤖 Claude conclusion */}
          <div className="border-2 border-skin-link rounded-sm px-2.5 py-1.5 bg-skin-base shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-skin-link font-semibold text-sm">🤖 Conclusion</span>
              {submitted && validDocId && CONVEX_CONFIGURED ? (
                <button type="button" onClick={onConclude} disabled={concluding}
                  className="px-2 py-0.5 text-xs border-2 border-skin-base rounded-sm hover:bg-skin-base-hover disabled:opacity-40">
                  {concluding ? "Analyzing…" : conclusion ? "Re-analyze" : "Analyze with Claude"}
                </button>
              ) : null}
              {conclusion?.ok ? (
                <button type="button" onClick={onCopyConclusion}
                  title="Copy the conclusion to the clipboard"
                  className="px-2 py-0.5 text-xs border-2 border-skin-base rounded-sm hover:bg-skin-base-hover">
                  {copied === true ? "✓ Copied to clipboard" : copied === "error" ? "⚠ Copy failed" : "📋 Copy"}
                </button>
              ) : null}
              {conclusion?.ok ? (
                <button type="button" onClick={() => setConclusionExpanded((v) => !v)}
                  title={conclusionExpanded ? "Collapse the conclusion" : "Expand to read the full conclusion without scrolling"}
                  className="px-2 py-0.5 text-xs border-2 border-skin-base rounded-sm hover:bg-skin-base-hover">
                  {conclusionExpanded ? "⤡ Collapse" : "⤢ Expand"}
                </button>
              ) : null}
            </div>
            <div className="text-xs opacity-90 mt-1 whitespace-pre-wrap wrap-break-word"
              style={{ maxHeight: conclusionExpanded ? "60vh" : 180, overflow: "auto" }}>
              {!submitted ? (
                "Enter an invoice and look it up, then click Analyze — Claude reads the assembled timeline and explains what happened, and why."
              ) : concluding ? (
                "Claude is analyzing the timeline…"
              ) : conclusion?.ok ? (
                conclusion.conclusion
              ) : conclusion?.error ? (
                <span style={{ color: "#ff6b6b" }}>{conclusion.error}</span>
              ) : !CONVEX_CONFIGURED ? (
                "The Claude conclusion requires the Convex backend (not configured in this build)."
              ) : (
                "Click “Analyze with Claude” to summarize this ticket in plain English."
              )}
            </div>
          </div>

          {/* 🕰 Time Machine history (on-demand). Mirrors the JobsDetailModal
              history feed — per-row expand/collapse — but read-only (no
              time-travel scrub) since there's no grid behind this modal.
              Reads PROD to match the rest of Ticket Master. */}
          {doc && validDocId ? (
            <div className="border border-skin-base rounded-sm bg-skin-base shrink-0">
              <button type="button" onClick={() => setShowHistory((v) => !v)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-skin-base-hover">
                <span className="text-xs opacity-70">{showHistory ? "▾" : "▸"}</span>
                <span aria-hidden="true">🕰</span>
                <span className="font-semibold text-sm">Time Machine history</span>
                <span className="text-xs opacity-55">
                  {showHistory ? "Scheduler change log + customer-portal queries" : "Click to load the full job history"}
                </span>
              </button>
              {showHistory ? (
                <div className="px-3 pb-2" style={{ maxHeight: "40vh", overflow: "auto" }}>
                  <JobHistorySection documentId={documentId} kellerId={kellerId} readOnly database={PROD_DB} />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Correlated timeline */}
          <TicketMasterTimeline
            active={Boolean(submitted)}
            eventLog={eventRows}
            schedule={scheduleRows}
            turboq={turboRows}
            errors={{ eventLog: eventError, schedule: scheduleError, turboq: turboError }}
            loading={loading}
            truncated={{ turboq: turboData?.turboq?.truncated ? (turboData.turboq.count || 0) : null }}
            order={order}
            schedulerOnly={schedulerOnly}
          />
        </div>
      </Modal.Content>
    </Modal>
  );
}

export default TicketMasterModal;
